import React, { useState, useEffect } from 'react';
import { useAuth } from '../auth/useAuth';
import { 
  Typography, 
  Box, 
  Alert,
  Snackbar,
  Fab,
  useTheme,
  useMediaQuery
} from '@mui/material';
import { 
  People as PeopleIcon,
  DirectionsBus as BusIcon,
  ConfirmationNumber as TicketIcon,
  AttachMoney as MoneyIcon,
  Folder as ProjectIcon,
  FlightTakeoff as TouristIcon,
  Refresh as RefreshIcon
} from '@mui/icons-material';
import AdminLayout from '../components/AdminLayout';
import KPICard from '../components/dashboard/KPICard';
import StatsCard from '../components/dashboard/StatsCard';
import AlertsPanel from '../components/dashboard/AlertsPanel';
import QuickStats from '../components/dashboard/QuickStats';
import DataTable from '../components/dashboard/DataTable';
import { KPISection, ChartsSection, DataSection } from '../components/dashboard/DashboardGrid';

const ManagerDashboard = () => {
  const { api } = useAuth();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  
  const [dashboardData, setDashboardData] = useState(null);
  const [closedBuses, setClosedBuses] = useState([]);
  const [initialLoading, setInitialLoading] = useState(true);
  const [backgroundRefreshing, setBackgroundRefreshing] = useState(false);
  const [error, setError] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);

  const fetchDashboardData = async (isBackgroundRefresh = false) => {
    try {
      if (!isBackgroundRefresh) {
        setInitialLoading(true);
      } else {
        setBackgroundRefreshing(true);
      }
      setError(null);
      
      const dashboardResponse = await api.get('/dashboard');
      setDashboardData(dashboardResponse.data);
      
      // נסה לטעון אוטובוסים סגורים, אם לא זמין - תמשיך בלי זה
      try {
        const closedBusesResponse = await api.get('/dashboard/closed-buses');
        setClosedBuses(closedBusesResponse.data);
      } catch (closedBusesError) {
        console.warn('Closed buses endpoint not available yet:', closedBusesError.message);
        setClosedBuses([]); // רשימה ריקה אם הendpoint לא זמין
      }
      
      setLastUpdated(new Date());
      } catch (error) {
        console.error('Failed to fetch dashboard data', error);
      setError('שגיאה בטעינת נתוני הדשבורד');
    } finally {
      if (!isBackgroundRefresh) {
        setInitialLoading(false);
      } else {
        setBackgroundRefreshing(false);
      }
    }
  };

  useEffect(() => {
    fetchDashboardData(); // טעינה ראשונית
    // רענון אוטומטי כל 30 שניות ברקע
    const interval = setInterval(() => fetchDashboardData(true), 30000);
    return () => clearInterval(interval);
  }, [api]);

  const calculateChange = (current, previous) => {
    if (!previous || previous === 0) return null;
    const change = ((current - previous) / previous) * 100;
    return `${change > 0 ? '+' : ''}${change.toFixed(1)}%`;
  };

  const getChangeType = (current, previous) => {
    if (!previous || previous === 0) return 'neutral';
    return current > previous ? 'positive' : current < previous ? 'negative' : 'neutral';
  };

  if (initialLoading && !dashboardData) {
    return (
      <AdminLayout>
        <Box sx={{ p: { xs: 2, md: 3 } }}>
          <Typography variant="h4" gutterBottom>
            דשבורד מנהלים
          </Typography>
          <KPISection>
            {Array.from({ length: 6 }).map((_, index) => (
              <KPICard key={index} loading={true} />
            ))}
          </KPISection>
        </Box>
      </AdminLayout>
    );
  }

  if (error) {
    return (
      <AdminLayout>
        <Box sx={{ p: { xs: 2, md: 3 } }}>
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        </Box>
      </AdminLayout>
    );
  }

  const {
    users = {},
    buses = {},
    passengers = {},
    financial = {},
    projects = {},
    tourists = {},
    realtime = {}
  } = dashboardData || {};

  return (
    <AdminLayout>
      <Box sx={{ pb: 10 }}> {/* מקום לFAB */}
        {/* Header */}
        <Box sx={{ 
          p: { xs: 2, md: 3 },
          borderBottom: 1,
          borderColor: 'divider',
          mb: 2
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Typography 
              variant={isMobile ? 'h5' : 'h4'} 
              gutterBottom
              sx={{ fontWeight: 'bold', mb: 0 }}
            >
              דשבורד מנהלים
            </Typography>
            {backgroundRefreshing && (
              <Box sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: 1,
                color: 'primary.main'
              }}>
                <Box
                  sx={{
                    width: 12,
                    height: 12,
                    borderRadius: '50%',
                    backgroundColor: 'primary.main',
                    animation: 'pulse 1.5s ease-in-out infinite',
                    '@keyframes pulse': {
                      '0%': { opacity: 1 },
                      '50%': { opacity: 0.5 },
                      '100%': { opacity: 1 }
                    }
                  }}
                />
                <Typography variant="caption" color="primary.main">
                  מתעדכן...
                </Typography>
              </Box>
            )}
          </Box>
          {lastUpdated && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              עודכן לאחרונה: {lastUpdated.toLocaleTimeString('he-IL')}
            </Typography>
          )}
        </Box>

        {/* KPI Cards */}
        <KPISection>
          <KPICard
            title="סה״כ משתמשים"
            value={users.totalUsers}
            change={calculateChange(users.activeUsers, users.totalUsers - users.activeUsers)}
            changeType={getChangeType(users.activeUsers, users.totalUsers - users.activeUsers)}
            icon={PeopleIcon}
            color="primary"
            loading={initialLoading}
          />
          <KPICard
            title="אוטובוסים פעילים"
            value={buses.activeBuses}
            change={buses.todayBuses ? `${buses.todayBuses} היום` : null}
            changeType="positive"
            icon={BusIcon}
            color="success"
            loading={initialLoading}
          />
          <KPICard
            title="תיקופים היום"
            value={passengers.validationsToday}
            change={`מתוך ${passengers.totalPassengers}`}
            changeType="positive"
            icon={TicketIcon}
            color="info"
            loading={initialLoading}
          />
          <KPICard
            title="הכנסות היום"
            value={financial.todayRevenue ? `$${financial.todayRevenue.toLocaleString()}` : '$0'}
            change={financial.dailyChangePercent ? `${financial.dailyChangePercent}%` : null}
            changeType={financial.dailyChangePercent > 0 ? 'positive' : 'negative'}
            icon={MoneyIcon}
            color="warning"
            loading={initialLoading}
          />
          <KPICard
            title="פרויקטים פעילים"
            value={projects.activeProjects}
            change={`מתוך ${projects.totalProjects}`}
            changeType="neutral"
            icon={ProjectIcon}
            color="secondary"
            loading={initialLoading}
          />
          <KPICard
            title="תיירים פעילים"
            value={tourists.activeTourists}
            change={tourists.validationRate ? `${tourists.validationRate}% תוקפו` : null}
            changeType="positive"
            icon={TouristIcon}
            color="primary"
            loading={initialLoading}
          />
        </KPISection>

        {/* Stats Cards */}
        <DataSection columns={1}>
          <StatsCard
            title="הכנסות לפי אמצעי תשלום"
            data={financial.revenueByMethod?.map(item => ({
              label: item.method === 'cash' ? 'מזומן' : 
                     item.method === 'credit' ? 'אשראי' : 
                     item.method === 'paypal' ? 'PayPal' : item.method,
              value: item.total
            })) || []}
            loading={initialLoading}
            showPercentages={true}
            colorPalette={['success', 'primary', 'warning']}
          />
        </DataSection>

        {/* Quick Stats & Alerts */}
        <DataSection columns={isMobile ? 1 : 2}>
          <QuickStats
            title="מעקב בזמן אמת"
            stats={[
              {
                label: 'משתמשים פעילים',
                value: realtime.activeUsers || 0,
                type: 'number',
                color: 'success',
                subtitle: 'בשעה האחרונה'
              },
              {
                label: 'תיקופים אחרונים',
                value: typeof realtime.recentValidations === 'number' ? realtime.recentValidations : 0,
                type: 'number',
                color: 'info',
                subtitle: 'ב-5 דקות האחרונות'
              },
              {
                label: 'טרנזקציות חדשות',
                value: realtime.recentTransactions || 0,
                type: 'number',
                color: 'warning',
                subtitle: 'ב-5 דקות האחרונות'
              },
              {
                label: 'שיעור תיקוף',
                value: tourists.validationRate || 0,
                type: 'percentage',
                color: 'primary',
                trend: 5.2,
                subtitle: 'מכלל התיירים'
              }
            ]}
            loading={initialLoading}
            lastUpdated={lastUpdated}
            showProgress={false}
          />
          
          <AlertsPanel
            alerts={realtime.systemAlerts || []}
            title="התראות מערכת"
            maxVisible={isMobile ? 3 : 5}
            showTimestamp={true}
          />
        </DataSection>

        {/* Closed Buses Table */}
        <DataSection columns={1}>
          <DataTable
            title="אוטובוסים שנסגרו לאחרונה"
            data={closedBuses}
            loading={initialLoading}
            columns={[
              {
                field: 'busNumber',
                headerName: 'מספר אוטובוס',
                minWidth: 100,
                align: 'center'
              },
              {
                field: 'destination',
                headerName: 'יעד',
                minWidth: 120,
                hideOnMobile: false
              },
              {
                field: 'origin',
                headerName: 'מוצא',
                minWidth: 120,
                hideOnMobile: true
              },
              {
                field: 'totalSeats',
                headerName: 'מקומות',
                type: 'number',
                minWidth: 80,
                align: 'center'
              },
              {
                field: 'validatedPassengers',
                headerName: 'תיקופים',
                type: 'number',
                minWidth: 80,
                align: 'center'
              },
              {
                field: 'fillPercentage',
                headerName: 'אחוז תפוסה',
                type: 'percentage',
                minWidth: 100,
                align: 'center'
              },
              {
                field: 'gapPercentage',
                headerName: 'אחוז פער',
                type: 'percentage',
                minWidth: 100,
                align: 'center',
                hideOnMobile: true
              },
              {
                field: 'duration',
                headerName: 'משך פעילות',
                minWidth: 120,
                align: 'center',
                hideOnMobile: true
              },
              {
                field: 'closedBy',
                headerName: 'נסגר על ידי',
                minWidth: 120,
                hideOnMobile: true
              }
            ]}
            dense={isMobile}
            maxHeight={400}
          />
        </DataSection>

        {/* Floating Action Button לרענון */}
        <Fab
          color="primary"
          aria-label="רענון"
          onClick={() => fetchDashboardData(false)} // רענון מלא
          disabled={initialLoading || backgroundRefreshing}
          sx={{
            position: 'fixed',
            bottom: { xs: 16, md: 24 },
            right: { xs: 16, md: 24 },
            zIndex: 1000,
            // אפקט אנימציה כשמתרענן ברקע
            ...(backgroundRefreshing && {
              animation: 'spin 2s linear infinite',
              '@keyframes spin': {
                '0%': { transform: 'rotate(0deg)' },
                '100%': { transform: 'rotate(360deg)' }
              }
            })
          }}
        >
          <RefreshIcon />
        </Fab>

        {/* Snackbar להודעות */}
        <Snackbar
          open={!!error}
          autoHideDuration={6000}
          onClose={() => setError(null)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        >
          <Alert onClose={() => setError(null)} severity="error">
            {error}
          </Alert>
        </Snackbar>
      </Box>
    </AdminLayout>
  );
};

export default ManagerDashboard;